import { checkSession } from "@features/auth";
import { useUserInfo } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Plus, TicketPercent } from "lucide-react";
import { type PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { z } from "zod";
import { AnonymousLockAccess, AnonymousLockAccessError } from "~/components/lock/AnonymousLockAccess";
import { formatB2CPrice, getB2CPricingEstimateDates, lowestB2CHourlyRate } from "~/components/lock/B2CCheckout/pricing";
import { MyLocksTabs } from "~/components/lock/MyLocks/MyLocksTabs";
import { ServiceBreadcrumb } from "~/components/lock/ServiceBreadcrumb";
import type { MyLocksTabsFragment$key } from "~/components/lock/MyLocks/__generated__/MyLocksTabsFragment.graphql";
import { useLockOpen } from "~/components/lock/MyLocks/useLockOpen";
import { useReservationLockOpen } from "~/components/lock/MyLocks/useReservationLockOpen";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { lockB2CRentalsQuery } from "./__generated__/lockB2CRentalsQuery.graphql";
import type { lockB2CAnonymousQuery } from "./__generated__/lockB2CAnonymousQuery.graphql";

const query = graphql`
	query lockB2CRentalsQuery($hubName: String!, $oneHour: DateTime!, $twoHours: DateTime!) @relay_test_operation {
		oneHourEstimate: b2CLockSessionFeeEstimate(input: { until: $oneHour })
		twoHoursEstimate: b2CLockSessionFeeEstimate(input: { until: $twoHours })
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			hub @required(action: THROW) {
				id
				status
			}
		}
		myUtilizationGrants(input: {}, pagination: { page: 0, pageSize: 1000 }) {
			nodes {
				...MyLocksTabsFragment
			}
		}
		b2CLockOutstandingSessions {
			id
			finalAmount
			amountCollected
		}
		b2CLockReservations: lockReservations(
			filter: { subtype: B2C }
			orderBy: [{ field: STARTED_AT, ordering: DESC }]
			pagination: { page: 0, pageSize: 1000 }
		) {
			nodes {
				id
				...MyLocksTabsOwnedFragment
			}
		}
	}
`;

const anonymousQuery = graphql`
	query lockB2CAnonymousQuery($accessToken: String!) @relay_test_operation {
		anonymousUtilizationGrant(accessToken: $accessToken) {
			...AnonymousLockAccessFragment
			...MyLocksTabsFragment
		}
	}
`;

type LoaderData =
	| { kind: "anonymous"; token: string; queryRef: PreloadedQuery<lockB2CAnonymousQuery> }
	| {
			kind: "authenticated";
			queryRef: PreloadedQuery<lockB2CRentalsQuery>;
			token?: string;
			anonymousQueryRef?: PreloadedQuery<lockB2CAnonymousQuery>;
	  };

export const Route = createFileRoute("/$hubMetaName/lock/")({
	beforeLoad: () => {
		document.title = "bacehub | Lock rentals";
	},
	component: RouteComponent,
	errorComponent: ({ error }) => <AnonymousLockAccessError error={error} service="LOCK_B2C" />,
	shouldReload: true,
	validateSearch: zodValidator(z.object({ token: z.string().optional() })),
	loader: async (ctx): Promise<LoaderData> => {
		const token = new URLSearchParams(ctx.location.searchStr).get("token");
		const sessionExists = await checkSession();
		if (!sessionExists && token) {
			return {
				kind: "anonymous",
				token,
				queryRef: await preloadQuery<lockB2CAnonymousQuery>(ctx.context.relayEnvironment, anonymousQuery, { accessToken: token }),
			};
		}
		if (!sessionExists) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/lock${ctx.location.searchStr}` },
			});
		}

		const estimateDates = getB2CPricingEstimateDates();
		return {
			kind: "authenticated",
			queryRef: await preloadQuery<lockB2CRentalsQuery>(ctx.context.relayEnvironment, query, {
				hubName: ctx.params.hubMetaName,
				oneHour: estimateDates.oneHour,
				twoHours: estimateDates.twoHours,
			}),
			token: token ?? undefined,
			anonymousQueryRef: token
				? await preloadQuery<lockB2CAnonymousQuery>(ctx.context.relayEnvironment, anonymousQuery, { accessToken: token })
				: undefined,
		};
	},
});

function RouteComponent() {
	const loaderData = Route.useLoaderData();
	if (loaderData.kind === "anonymous") {
		return <AnonymousRoute loaderData={loaderData} />;
	}
	if (loaderData.anonymousQueryRef && loaderData.token) {
		return <AuthenticatedRouteWithToken loaderData={loaderData} token={loaderData.token} queryRef={loaderData.anonymousQueryRef} />;
	}
	return <AuthenticatedRoute loaderData={loaderData} />;
}

function AnonymousRoute(props: { loaderData: Extract<LoaderData, { kind: "anonymous" }> }) {
	const { anonymousUtilizationGrant } = usePreloadedQuery<lockB2CAnonymousQuery>(anonymousQuery, props.loaderData.queryRef);
	return <AnonymousLockAccess token={props.loaderData.token} grant={anonymousUtilizationGrant ?? undefined} service="LOCK_B2C" />;
}

function AuthenticatedRouteWithToken(props: {
	loaderData: Extract<LoaderData, { kind: "authenticated" }>;
	token: string;
	queryRef: PreloadedQuery<lockB2CAnonymousQuery>;
}) {
	const { anonymousUtilizationGrant } = usePreloadedQuery<lockB2CAnonymousQuery>(anonymousQuery, props.queryRef);
	return <AuthenticatedRoute loaderData={props.loaderData} anonymousGrant={anonymousUtilizationGrant} anonymousToken={props.token} />;
}

function AuthenticatedRoute(props: {
	loaderData: Extract<LoaderData, { kind: "authenticated" }>;
	anonymousGrant?: MyLocksTabsFragment$key[number] | null;
	anonymousToken?: string;
}) {
	const navigate = useNavigate();
	const me = useUserInfo();
	const i18n = useI18nContext().LL["new-ui"];
	const { hubMeta, myUtilizationGrants, b2CLockReservations, b2CLockOutstandingSessions, oneHourEstimate, twoHoursEstimate } =
		usePreloadedQuery<lockB2CRentalsQuery>(query, props.loaderData.queryRef);
	const outstanding = b2CLockOutstandingSessions[0];
	const outstandingAmount = outstanding ? Math.max((outstanding.finalAmount ?? 0) - outstanding.amountCollected, 0) / 100 : 0;
	const lowestHourlyRate = lowestB2CHourlyRate({ oneHour: oneHourEstimate, twoHours: twoHoursEstimate });
	const lockOpen = useLockOpen("LOCK_B2C");
	const ownedLockOpen = useReservationLockOpen("LOCK_B2C");

	if (lockOpen.overlay) return lockOpen.overlay;
	if (ownedLockOpen.overlay) return ownedLockOpen.overlay;

	return (
		<PageContainer
			className="min-h-dvh bg-slate-50"
			reserveFooterSpace
			footer={
				<Button
					className="w-full shadow-xl"
					size="lg"
					onClick={() => navigate({ to: "/$hubMetaName/lock/new", params: { hubMetaName: hubMeta.name } })}
				>
					<Plus className="size-4" />
					<T>{i18n.LOCK_RENT_COMPARTMENT()}</T>
				</Button>
			}
		>
			<AppHeader service="LOCK_B2C" />
			<main className="mx-auto flex w-full max-w-[390px] flex-col gap-4 px-6 pt-6">
				<div className="flex flex-col gap-2">
					<ServiceBreadcrumb hubMetaName={hubMeta.name} current={i18n.LOCK_B2C_RENTALS_TITLE()} />
					<h1 className="text-2xl font-breit font-medium leading-8">
						<T>{i18n.LOCK_B2C_RENTALS_TITLE()}</T>
					</h1>
				</div>
				{outstanding ? (
					<div role="alert" className="flex flex-col gap-3 rounded-[10px] border border-destructive/40 bg-destructive/5 p-4">
						<p className="text-sm">
							<T>{i18n.LOCK_B2C_SETTLE_BANNER({ amount: formatB2CPrice(outstandingAmount) })}</T>
						</p>
						<Button
							variant="destructive"
							onClick={() =>
								navigate({
									to: "/$hubMetaName/lock/pay/$sessionId",
									params: { hubMetaName: hubMeta.name, sessionId: outstanding.id },
								})
							}
						>
							<T>{i18n.LOCK_B2C_SETTLE_BANNER_ACTION()}</T>
						</Button>
					</div>
				) : null}
				<Button
					variant="outline"
					className="h-auto w-full justify-start gap-3 rounded-[12px] border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-950 hover:bg-emerald-100 hover:text-emerald-950"
					onClick={() => navigate({ to: "/$hubMetaName/lock/coupons", params: { hubMetaName: hubMeta.name } })}
				>
					<TicketPercent className="size-5 shrink-0 text-emerald-700" />
					<span className="flex-1 text-left">
						<span className="block font-medium">
							<T>{i18n.LOCK_COUPON_ENTRY_TITLE()}</T>
						</span>
						<span className="block text-xs font-normal text-emerald-900/75">
							<T>{i18n.LOCK_COUPON_ENTRY_DESCRIPTION()}</T>
						</span>
					</span>
				</Button>
				<MyLocksTabs
					grants={myUtilizationGrants.nodes}
					anonymousGrant={props.anonymousGrant}
					anonymousToken={props.anonymousToken}
					ownedReservations={b2CLockReservations.nodes}
					hubId={hubMeta.hub.id}
					userId={me?.id}
					isOpening={lockOpen.isOpening || ownedLockOpen.isOpening}
					openedGrantId={lockOpen.openedGrantId}
					onOpen={lockOpen.requestOpen}
					onEndRental={(grant) =>
						navigate({
							to: "/$hubMetaName/lock/end-rental/$reservationId",
							search: { source: "b2c" },
							params: { hubMetaName: hubMeta.name, reservationId: grant.reservation.id },
						})
					}
					onOpenOwned={(reservation) => ownedLockOpen.requestOpen(reservation.id)}
					onEndOwnedRental={(reservation) =>
						navigate({
							to: "/$hubMetaName/lock/end-rental/$reservationId",
							params: { hubMetaName: hubMeta.name, reservationId: reservation.id },
							search: { source: "b2c" },
						})
					}
					openedReservationId={ownedLockOpen.openedReservationId}
					subtype="B2C"
					activeEmptyState={
						<div className="flex flex-col gap-4 rounded-[10px] border p-6">
							<p className="text-sm font-medium">
								<T>{i18n.LOCK_B2C_EMPTY_ACTIVE_TITLE()}</T>
							</p>
							<p className="text-center text-sm text-muted-foreground">
								<T>{i18n.LOCK_B2C_EMPTY_ACTIVE_DESC()}</T>
								{lowestHourlyRate !== null ? (
									<>
										{" "}
										<T>{i18n.LOCK_B2C_EMPTY_ACTIVE_PRICE({ price: formatB2CPrice(lowestHourlyRate) })}</T>
									</>
								) : null}
							</p>
						</div>
					}
				/>
			</main>
			{lockOpen.confirmDialog}
			{ownedLockOpen.confirmDialog}
		</PageContainer>
	);
}
